import React, { useState, useCallback } from "react";
import {
  useUpdateMany,
  useRefresh,
  useNotify,
  useUnselectAll,
  useListContext,
} from "react-admin";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import LoadingButton from "@mui/lab/LoadingButton";
import pickBy from "lodash/pickBy";
import { z } from "zod";
import EditIcon from "@mui/icons-material/Edit";

import Loader from "../Loader";
import { useCustomAllRecords } from "../Common/useCustomAllRecords";
import NewNumberInput from "../Common/NewNumberInput";
import { keywordSchema } from "../../models/Keyword";
import { useIsBusinessPlan } from "../../helpers/subscriptionPlan";

const keywordsSchema = z.array(keywordSchema);

const enablerDefaultState = {
  isActive: false,
  allowInWeb: false,
  allowBackorder: false,
  price: false,
  webPrice: false,
  cost: false,
  stock: false,
  stockLeft: false,
  // tieredPrices: false,
  // webTieredPrices: false,
  weight: false,
  detail: false,
  minQuantity: false,
  maxQuantity: false,
  sku: false,
};

const defaultFieldState = {
  isActive: false,
  allowInWeb: false,
  allowBackorder: false,
  price: 0,
  webPrice: 0,
  cost: 0,
  stock: 0,
  stockLeft: 0,
  // tieredPrices: "",
  // webTieredPrices: "",
  weight: 0,
  detail: "",
  minQuantity: 1,
  maxQuantity: -1,
  sku: "",
};

const fields = [
  { label: "isActive", type: "switch", name: "isActive" },
  { label: "allowInWeb", type: "switch", name: "allowInWeb" },
  { label: "allowBackorder", type: "switch", name: "allowBackorder" },
  { label: "price", type: "decimal", name: "price" },
  { label: "webPrice", type: "decimal", name: "webPrice" },
  { label: "cost", type: "decimal", name: "cost" },
  {
    label: "vipTier",
    type: "positiveInteger",
    name: "vipTier",
    requiresBusinessPlan: true,
  },
  { label: "stock", type: "positiveInteger", name: "stock" },
  { label: "stockLeft", type: "positiveInteger", name: "stockLeft" },
  { label: "weight(Kg)", type: "decimal", name: "weight" },
  { label: "detail", type: "text", name: "detail" },
  {
    label: "minQuantity",
    type: "number",
    name: "minQuantity",
    requiresBusinessPlan: true,
  },
  {
    label: "maxQuantity",
    type: "number",
    name: "maxQuantity",
    requiresBusinessPlan: true,
  },
  { label: "sku", type: "text", name: "sku" },
];

const BulkUpdateKeywordsButton = () => {
  const { resource, selectedIds } = useListContext();
  const [enabler, setEnabler] = React.useState(enablerDefaultState);
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState(defaultFieldState);
  const allRecords = useCustomAllRecords();

  const refresh = useRefresh();
  const notify = useNotify();
  const unselectAll = useUnselectAll(resource);
  const isBusinessPlan = useIsBusinessPlan();
  const [updateMany, { isLoading, error }] = useUpdateMany();

  const toggleHandler = useCallback(
    (event) => {
      setEnabler({ ...enabler, [event.target.name]: event.target.checked });
    },
    [enabler]
  );

  const handleClick = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    unselectAll();
    refresh();
    setIsOpen(false);
    setEnabler(enablerDefaultState);
    setData(defaultFieldState);
  }, [refresh, unselectAll]);

  const change = Object.keys(enabler)
    .filter((key) => enabler[key])
    .reduce((acc, key) => {
      acc[key] = data[key];
      return acc;
    }, {});

  const previews = selectedIds.map((id) => {
    const originalRecord = allRecords.find((record) => record._id === id);
    return {
      ...originalRecord,
      ...change,
    };
  });
  const zResult = keywordsSchema.safeParse(previews);
  React.useEffect(() => {
    return () => {
      setData(defaultFieldState);
      unselectAll();
    };
  }, [resource, unselectAll]);

  const handleConfirm = useCallback(() => {
    // payload to hold only the fields that are checked
    const payload = pickBy(data, (v, k) => enabler[k]);

    updateMany(
      "keywords",
      {
        ids: selectedIds,
        data: payload,
      },
      {
        onSuccess: () => {
          refresh();
          unselectAll();
          notify("Updated successfully");
          setIsOpen(false);
        },
        onFailure: () => {
          notify("Update failed, please retry later", { type: "error" });
        },
      }
    );
  }, [data, enabler, notify, refresh, selectedIds, unselectAll, updateMany]);

  // const hasEmptyField = Object.keys(enabler).some(
  //   (fieldName) =>
  //     enabler[fieldName] && (!data[fieldName] || data[fieldName] === "")
  // );
  // console.log(zResult.error?.issues);
  return (
    <>
      <Button label="edit" onClick={handleClick}>
        <EditIcon />
        Edit
      </Button>
      <Dialog open={isOpen} aria-labelledby="responsive-dialog-title">
        <DialogTitle>{"Select field(s) to edit"}</DialogTitle>
        <DialogContent style={{ display: "flex", flexDirection: "column" }}>
          {zResult.error && <h4>Errors:</h4>}
          {zResult.error &&
            zResult.error.issues.map((issue, idx) => (
              <p key={`${issue.message}${idx}`}>{issue.message}</p>
            ))}
          {fields.map((field, index) => {
            const error =
              !zResult.success &&
              zResult.error.issues.find((i) => i.path.includes(field.name));
            const { requiresBusinessPlan = false } = field;

            return (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "1rem",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={enabler[field.name]}
                      onChange={toggleHandler}
                      name={field.name}
                      disabled={requiresBusinessPlan && !isBusinessPlan}
                    />
                  }
                  label={field.label}
                  style={{ marginRight: "1rem" }}
                />
                {field.type === "switch" && (
                  <Switch
                    checked={data[field.name]}
                    onChange={(e) =>
                      setData({ ...data, [field.name]: e.target.checked })
                    }
                    disabled={!enabler[field.name]}
                    name={field.name}
                    color="primary"
                  />
                )}
                {(field.type === "text" || field.type === "number") && (
                  <TextField
                    label={field.label}
                    type={field.type}
                    value={data[field.name]}
                    onChange={(e) =>
                      setData({ ...data, [field.name]: e.target.value })
                    }
                    disabled={!enabler[field.name]}
                    fullWidth
                    style={{ marginTop: "0.5rem" }}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
                {field.type === "decimal" && (
                  <NewNumberInput
                    label={field.label}
                    variant="outlined"
                    decimals={field.type === "decimal" ? 2 : undefined}
                    value={data[field.name]}
                    onChangeValue={(v) => {
                      setData({
                        ...data,
                        [field.name]: v,
                      });
                    }}
                    style={{ marginTop: "0.5rem" }}
                    fullWidth
                    disabled={!enabler[field.name]}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
                {field.type === "positiveInteger" && (
                  <NewNumberInput
                    label={field.label}
                    variant="outlined"
                    value={data[field.name]}
                    onChangeValue={(v) => {
                      setData({
                        ...data,
                        [field.name]: v,
                      });
                    }}
                    style={{ marginTop: "0.5rem" }}
                    fullWidth
                    disabled={!enabler[field.name]}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              </div>
            );
          })}
        </DialogContent>
        <DialogActions>
          <LoadingButton
            loading={isLoading}
            variant="contained"
            onClick={handleConfirm}
            color="primary"
            disabled={
              Object.values(enabler).every((value) => !value) || //if no enabler
              isLoading ||
              !zResult.success
            }
          >
            Update
          </LoadingButton>
          <Button
            variant="outlined"
            onClick={handleDialogClose}
            color="secondary"
            disabled={isLoading}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <Loader open={isLoading} />
    </>
  );
};

export default BulkUpdateKeywordsButton;
